import React, {
  useCallback,
  forwardRef,
  ComponentPropsWithoutRef,
  ElementType,
} from 'react';
import classnames from 'classnames';
import { Link } from 'react-router-dom';

import { ADD_FEATURE_TYPE, compilePath } from '../../utils/helpers/routes';
import Icon from '../icon/Icon';
import CloseButton from '../close-button/CloseButton';
import Responsive from '../responsive/Responsive';

import './AddPlaceCategories.scss';

const toAddress = (loc) => ({
  pathname: compilePath('add', null, { featureType: ADD_FEATURE_TYPE.address }),
  search: loc.search,
});

const toBusiness = (loc) => ({
  pathname: compilePath('add', null, {
    featureType: ADD_FEATURE_TYPE.business,
  }),
  search: loc.search,
});

const toRoad = (loc) => ({
  pathname: compilePath('add', null, { featureType: ADD_FEATURE_TYPE.road }),
  search: loc.search,
});

const toSpeedLimit = (loc) => ({
  pathname: compilePath('add', null, {
    featureType: ADD_FEATURE_TYPE.speedLimit,
  }),
  search: loc.search,
});

const toIncident = (loc) => ({
  pathname: compilePath('add', null, {
    featureType: ADD_FEATURE_TYPE.incident,
  }),
  search: loc.search,
});

const toClearance = (loc) => ({
  pathname: compilePath('add', null, {
    featureType: ADD_FEATURE_TYPE.clearance,
  }),
  search: loc.search,
});

export interface AddPlaceCategoriesProps
  extends ComponentPropsWithoutRef<ElementType<any>> {
  className: string;
  onCloseButtonClick: () => void;
  title: string;
}

const AddPlaceCategories = (
  {
    className,
    onCloseButtonClick,
    title = 'What do you want to add?',
  }: AddPlaceCategoriesProps,
  ref,
) => {
  const handleAddAddressClick = useCallback(
    () => analytics.track('Open Add Address', { from: 'map' }),
    [],
  );

  const handleAddBusinessClick = useCallback(
    () => analytics.track('Open Add Business', { from: 'map' }),
    [],
  );

  const handleAddRoadClick = useCallback(
    () => analytics.track('Open Add Road', { from: 'map' }),
    [],
  );

  const handleAddSpeedLimitClick = useCallback(
    () => analytics.track('Open Add Speed limit', { from: 'map' }),
    [],
  );

  const handleAddIncidentClick = useCallback(
    () => analytics.track('Open Add Incident', { from: 'map' }),
    [],
  );

  const handleAddClearanceClick = useCallback(
    () => analytics.track('Open Add Clearance', { from: 'map' }),
    [],
  );

  const addAddressLink = (
    <Link
      to={toAddress}
      className="add-place-categories__button add-place-categories__button-address"
      onClick={handleAddAddressClick}
    >
      <div className="add-place-categories__icon-container">
        <Icon className="add-place-categories__icon" name="pin-curvy" />
      </div>
      <div className="add-place-categories__button-label">Address</div>
    </Link>
  );

  const addBusinessLink = (
    <Link
      to={toBusiness}
      className="add-place-categories__button add-place-categories__button-business"
      onClick={handleAddBusinessClick}
    >
      <div className="add-place-categories__icon-container">
        <Icon className="add-place-categories__icon" name="building" />
      </div>
      <div className="add-place-categories__button-label">Business</div>
    </Link>
  );

  const addSpeedLimitLink = (
    <Link
      to={toSpeedLimit}
      className="add-place-categories__button add-place-categories__button-speed-limit"
      onClick={handleAddSpeedLimitClick}
    >
      <div className="add-place-categories__icon-container">
        <Icon className="add-place-categories__icon" name="speed-limit" />
      </div>
      <div className="add-place-categories__button-label">Speed limit</div>
    </Link>
  );

  const addRoadLink = (
    <Link
      to={toRoad}
      className="add-place-categories__button add-place-categories__button-road"
      onClick={handleAddRoadClick}
    >
      <div className="add-place-categories__icon-container">
        <Icon className="add-place-categories__icon" name="road" />
      </div>
      <div className="add-place-categories__button-label">Road</div>
    </Link>
  );

  const addIncidentLink = (
    <Link
      to={toIncident}
      className="add-place-categories__button add-place-categories__button-incident"
      onClick={handleAddIncidentClick}
    >
      <div className="add-place-categories__icon-container">
        <Icon className="add-place-categories__icon" name="incident" />
      </div>
      <div className="add-place-categories__button-label">Incident</div>
    </Link>
  );

  const addClearanceLink = (
    <Link
      to={toClearance}
      className="add-place-categories__button add-place-categories__button-clearance"
      onClick={handleAddClearanceClick}
    >
      <div className="add-place-categories__icon-container">
        <Icon className="add-place-categories__icon" name="clearance" />
      </div>
      <div className="add-place-categories__button-label">Clearance</div>
    </Link>
  );

  return (
    <div className={classnames('add-place-categories', className)} ref={ref}>
      {onCloseButtonClick && (
        <CloseButton
          className="add-place-categories__close-button"
          onClick={onCloseButtonClick}
        />
      )}
      {title && <h1 className="add-place-categories__title">{title}</h1>}
      <div className="add-place-categories__grid">
        <Responsive mobile={false}>
          <div className="add-place-categories__grid-row">
            {addAddressLink}
            {addBusinessLink}
            {addRoadLink}
          </div>
          <div className="add-place-categories__grid-row">
            {addSpeedLimitLink}
            {addIncidentLink}
            {addClearanceLink}
          </div>
        </Responsive>
        <Responsive>
          <div className="add-place-categories__grid-row">
            {addAddressLink}
            {addBusinessLink}
          </div>
          <div className="add-place-categories__grid-row">
            {addRoadLink}
            {addSpeedLimitLink}
          </div>
          <div className="add-place-categories__grid-row">
            {addIncidentLink}
            {addClearanceLink}
          </div>
        </Responsive>
      </div>
    </div>
  );
};

export default forwardRef(AddPlaceCategories);
